/**
 * The last successful quickfetch response for an org — the dashboard's
 * above-the-fold data — persisted so the NEXT visit can paint it before any
 * network request completes (lib/prefetch/hydratePersistedQueries.ts), then
 * revalidate through the normal prefetch.
 *
 * Written by the dashboard prefetch entry once its response is in hand; read
 * at parse time by the hydration step; cleared on logout with every other
 * per-user cache. One org per browser (the most recently visited) keeps the
 * localStorage footprint bounded.
 *
 * Day-scoped lists (meetings, reminders, tasks) carry the calendar day they
 * were fetched for; the hydration step only replays them on that same day.
 * Proposals, onboarding, import and flag state are day-independent and replay
 * within MAX_AGE_MS regardless.
 */

import type { HiddenMeetings } from '@/lib/dashboard/hiddenMeetings';
import type { DashboardQuickfetchResponse } from '@/lib/prefetch/entries/dashboardQuickfetchTypes';
import type { CrmObjectsListResponse } from '@/types/crm';

import {
  clearAllPersistedCacheByPrefix,
  getPersistedCache,
  setPersistedCache,
} from './persistedCache';

const CACHE_VERSION = 1;
const STORAGE_KEY_PREFIX = 'ahoy-dashboard-fold-';
const COMPONENT = 'persisted-dashboard-fold';

/** Day-independent parts replay for a day; older snapshots are ignored. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface PersistedDashboardFold {
  /** CRM entity id of the user the snapshot belongs to (null when unresolved). */
  userEntityId: string | null;
  /** IANA zone the day-scoped lists were fetched for. */
  timezone: string;
  /** `YYYY-MM-DD` in `timezone` — the day the lists cover. */
  dayKey: string;
  /** Meetings/reminders/tasks bounds the lists were fetched with. */
  rangeStartISO: string;
  rangeEndISO: string;
  reminderStartDate: string;
  reminderEndDate: string;
  integrationEntityIds: string[];
  hiddenMeetings: HiddenMeetings;
  proposals: NonNullable<DashboardQuickfetchResponse['proposals']> | null;
  meetings: CrmObjectsListResponse | null;
  reminders: CrmObjectsListResponse | null;
  tasks: CrmObjectsListResponse | null;
  onboardingStatus: Record<string, unknown> | null;
  importJobs: DashboardQuickfetchResponse['import_jobs'];
  featureFlags: string[] | null;
  reminderAutomationEnabled: boolean | null;
}

/** Row counts the skeleton uses to reserve the right height before data. */
export interface PersistedDashboardFoldCounts {
  proposals: number;
  meetings: number;
  reminders: number;
  tasks: number;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(el => typeof el === 'string');
}

function isListResponseOrNull(
  value: unknown
): value is CrmObjectsListResponse | null {
  if (value === null) {
    return true;
  }
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { results?: unknown }).results)
  );
}

function isFoldShape(raw: unknown): raw is PersistedDashboardFold {
  if (typeof raw !== 'object' || raw === null) {
    return false;
  }
  const obj = raw as Record<string, unknown>;
  if (obj.userEntityId !== null && typeof obj.userEntityId !== 'string') {
    return false;
  }
  if (
    typeof obj.timezone !== 'string' ||
    typeof obj.dayKey !== 'string' ||
    typeof obj.rangeStartISO !== 'string' ||
    typeof obj.rangeEndISO !== 'string' ||
    typeof obj.reminderStartDate !== 'string' ||
    typeof obj.reminderEndDate !== 'string'
  ) {
    return false;
  }
  if (!isStringArray(obj.integrationEntityIds)) {
    return false;
  }
  const hidden = obj.hiddenMeetings as Record<string, unknown> | null;
  if (
    typeof hidden !== 'object' ||
    hidden === null ||
    !isStringArray(hidden.seriesIds) ||
    !isStringArray(hidden.meetingEntityIds)
  ) {
    return false;
  }
  if (
    obj.proposals !== null &&
    (typeof obj.proposals !== 'object' ||
      !Array.isArray((obj.proposals as { results?: unknown }).results))
  ) {
    return false;
  }
  return (
    isListResponseOrNull(obj.meetings) &&
    isListResponseOrNull(obj.reminders) &&
    isListResponseOrNull(obj.tasks)
  );
}

export function getPersistedDashboardFold(
  orgId: number | undefined
): PersistedDashboardFold | undefined {
  if (orgId === undefined) {
    return undefined;
  }
  return getPersistedCache<PersistedDashboardFold>(
    `${STORAGE_KEY_PREFIX}${orgId}`,
    CACHE_VERSION,
    isFoldShape,
    COMPONENT,
    MAX_AGE_MS
  );
}

/**
 * Row counts from the persisted snapshot, or undefined when there is none.
 * Cheap enough to call from a skeleton's render path.
 */
export function getPersistedDashboardFoldCounts(
  orgId: number | undefined
): PersistedDashboardFoldCounts | undefined {
  const fold = getPersistedDashboardFold(orgId);
  if (!fold) {
    return undefined;
  }
  return {
    proposals: fold.proposals?.results.length ?? 0,
    meetings: fold.meetings?.results.length ?? 0,
    reminders: fold.reminders?.results.length ?? 0,
    tasks: fold.tasks?.results.length ?? 0,
  };
}

/** Persist the snapshot for `orgId`, dropping any other org's snapshot. */
export function setPersistedDashboardFold(
  orgId: number,
  value: PersistedDashboardFold
): void {
  const key = `${STORAGE_KEY_PREFIX}${orgId}`;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const existing = localStorage.key(i);
      if (
        existing &&
        existing.startsWith(STORAGE_KEY_PREFIX) &&
        existing !== key
      ) {
        localStorage.removeItem(existing);
      }
    }
  } catch {
    // Storage unavailable — setPersistedCache logs the write failure below.
  }
  setPersistedCache<PersistedDashboardFold>(
    key,
    value,
    CACHE_VERSION,
    COMPONENT
  );
}

export function clearAllPersistedDashboardFold(): void {
  clearAllPersistedCacheByPrefix(STORAGE_KEY_PREFIX, COMPONENT);
}
